import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Modal, PanelGroup, Panel, designSystem } from '@yola/ws-ui';
import UnsplashGalleryContainer from '../containers/unsplash-gallery-container';
import IstockGalleryContainer from '../containers/istock-gallery-container';
import stockIds from '../constants/stock-ids';

const {
  DialogHeader,
  DialogHeaderTitle,
  DialogButtonGroup,
  DialogButtonGroupSeparator,
  DialogButton,
  Tabs,
  TabsList,
  Tab,
  TabsContentList,
  TabContent,
} = designSystem;

const MODAL_WIDTH = 1440;
const MODAL_HEIGHT = 800;

function StockGalleryDialog({
  tabs,
  activeTabId,
  captions: { title, submit, cancel },
  sharedSearchQuery,
  isMobile,
  multiple = false,
  onSubmit,
  onClose,
  onTabChange = () => {},
  updateSelectedItems,
  updateSearchQuery,
  onSearchFocus,
  onSearchEnd,
  onLoadError,
  getInitialSearchQuery,
}) {
  const hasMultipleTabs = tabs.length > 1;
  const dialogClasses = classNames('ws-stock-gallery-dialog', {
    'ws-stock-gallery-dialog--with-tabs': hasMultipleTabs,
  });

  const renderUnsplashTabContent = ({ isActive }) => (
    <UnsplashGalleryContainer
      key={stockIds.UNSPLASH}
      sharedSearchQuery={sharedSearchQuery}
      multiple={multiple}
      isActive={isActive}
      isMobile={isMobile}
      onFocus={onSearchFocus}
      updateSelectedItems={updateSelectedItems}
      updateSearchQuery={updateSearchQuery}
      onSearchEnd={onSearchEnd}
      onLoadError={onLoadError}
      getInitialSearchQuery={getInitialSearchQuery}
    />
  );

  const renderIstockTabContent = ({ isActive }) => (
    <IstockGalleryContainer
      key={stockIds.ISTOCK}
      sharedSearchQuery={sharedSearchQuery}
      multiple={multiple}
      isActive={isActive}
      isMobile={isMobile}
      onFocus={onSearchFocus}
      updateSelectedItems={updateSelectedItems}
      updateSearchQuery={updateSearchQuery}
      onSearchEnd={onSearchEnd}
      onLoadError={onLoadError}
      getInitialSearchQuery={getInitialSearchQuery}
    />
  );

  const tabRenderers = {
    [stockIds.UNSPLASH]: renderUnsplashTabContent,
    [stockIds.ISTOCK]: renderIstockTabContent,
  };

  const renderTabContent = (tabId) => tabRenderers[tabId]({ isActive: activeTabId === tabId });

  return (
    <Modal
      className={dialogClasses}
      width={MODAL_WIDTH}
      height={MODAL_HEIGHT}
      fullscreen={isMobile}
      centered
      draggable={false}
      resizable={false}
      overlay="visible"
      handleCancel={onClose}
      handleSubmit={onSubmit}
    >
      <PanelGroup className="ws-stock-gallery-dialog__panel-group" height="100%">
        <Panel className="ws-stock-gallery-dialog__top-panel">
          <DialogHeader>
            <DialogHeaderTitle>{title}</DialogHeaderTitle>
          </DialogHeader>
        </Panel>
        <div className="ws-stock-gallery-dialog__body">
          {hasMultipleTabs ? (
            <Tabs
              activeTabId={activeTabId}
              appearance="medium"
              onTabChange={onTabChange}
              disableSwipeableContent
              noSideSpacing
              noVerticalSpacing
              noOverflow
            >
              <TabsList>
                {tabs.map((tab) => (
                  <Tab key={tab.id} tabId={tab.id} label={tab.label} />
                ))}
              </TabsList>
              <TabsContentList>
                {tabs.map((tab) => (
                  <TabContent key={tab.id} tabId={tab.id}>
                    {renderTabContent(tab.id)}
                  </TabContent>
                ))}
              </TabsContentList>
            </Tabs>
          ) : (
            renderTabContent(activeTabId)
          )}
        </div>
        <Panel className="ws-stock-gallery-dialog__footer">
          <DialogButtonGroup>
            <DialogButton onClick={onSubmit}>{submit}</DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={onClose}>{cancel}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
}

StockGalleryDialog.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  activeTabId: PropTypes.string.isRequired,
  captions: PropTypes.shape({
    title: PropTypes.string,
    submit: PropTypes.string,
    cancel: PropTypes.string,
  }).isRequired,
  sharedSearchQuery: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]).isRequired,
  isMobile: PropTypes.bool.isRequired,
  multiple: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onTabChange: PropTypes.func,
  updateSelectedItems: PropTypes.func.isRequired,
  updateSearchQuery: PropTypes.func.isRequired,
  onSearchFocus: PropTypes.func.isRequired,
  onSearchEnd: PropTypes.func.isRequired,
  onLoadError: PropTypes.func.isRequired,
  getInitialSearchQuery: PropTypes.func.isRequired,
};

export default StockGalleryDialog;
